import { useState, useMemo, useCallback, useId, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { FAQ_CATEGORIES, FAQ_ITEM_DEFS, FAQ_POPULAR_IDS, resolveFaqItemById, searchFaqItems, type FaqItem } from '../../data/faqData';
import { FaqVisual } from './FaqVisual';
import { highlightMatch } from './faqHighlight';
import './faq-ui.css';

const FAQ_VISUAL_LABELS: Record<string, string> = {
  none: '',
  hero: 'Getting started',
  projects: 'Projects & sessions',
  workspace: 'Workspace layout',
  ai: 'AI generation',
  chat: 'Chat & messages',
  gallery: 'Gallery & previews',
  media: 'Media & playback',
  search: 'Search & filters',
  profile: 'Profile & channel',
  settings: 'Settings panel',
  security: 'Security & privacy',
  calendar: 'Calendar & schedule',
  community: 'Groups & community',
  workflow: 'Workflow & handoff',
  platform: 'Portal & platforms',
  editor: 'Editor & timeline',
  render: 'Export & render',
  calls: 'Voice & video calls',
  studio: 'Studio overview',
  piano: 'Piano roll',
  transport: 'Transport & loop',
  markers: 'Section markers',
  sequence: 'Sequence & arrangement',
  session: 'Session JSON',
  mixer: 'Mixer & levels',
  effects: 'Effects chain',
  generate: 'AI generate',
  mix: 'Mix & master',
  record: 'Recording',
  groove: 'Groove templates',
  waveform: 'Waveform view',
};
const FAQ_PARA_LABELS = ['Overview', 'How to use', 'Details & tips', 'Troubleshooting'];

function categoryIcon(categoryId: string): string {
  const c = FAQ_CATEGORIES.find((x) => x.id === categoryId);
  return c?.icon ?? '❔';
}

function faqItemUrl(itemId: string): string {
  return new URL(`/faq?id=${encodeURIComponent(itemId)}`, window.location.origin).href;
}

interface FaqExplorerProps {
  variant?: 'page' | 'modal';
  initialQuery?: string;
  initialItemId?: string;
  initialCategoryId?: string;
}

export function FaqExplorer({
  variant = 'page',
  initialQuery = '',
  initialItemId = '',
  initialCategoryId = '',
}: FaqExplorerProps) {
  const { t, i18n } = useTranslation();
  const baseId = useId();
  const [query, setQuery] = useState(initialQuery);
  const validCategory =
    initialCategoryId && FAQ_CATEGORIES.some((c) => c.id === initialCategoryId)
      ? initialCategoryId
      : 'all';
  const [categoryId, setCategoryId] = useState<string>(validCategory);
  const [openIds, setOpenIds] = useState<Set<string>>(() => new Set());
  const [scrollTargetId, setScrollTargetId] = useState<string | null>(null);
  const itemRefs = useRef<Record<string, HTMLElement | null>>({});
  const searchRef = useRef<HTMLInputElement>(null);

  const filtered = useMemo(
    () => searchFaqItems(t, query, categoryId),
    [t, i18n.language, query, categoryId],
  );

  const filteredIds = useMemo(() => filtered.map((item) => item.id), [filtered]);
  const allFilteredOpen = filtered.length > 0 && filtered.every((item) => openIds.has(item.id));

  useEffect(() => {
    setQuery(initialQuery);
  }, [initialQuery]);

  useEffect(() => {
    if (initialItemId) return;
    if (initialCategoryId && FAQ_CATEGORIES.some((c) => c.id === initialCategoryId)) {
      setCategoryId(initialCategoryId);
    }
  }, [initialCategoryId, initialItemId]);

  useEffect(() => {
    if (initialItemId && FAQ_ITEM_DEFS.some((d) => d.id === initialItemId)) {
      setOpenIds(new Set([initialItemId]));
      setScrollTargetId(initialItemId);
      const def = FAQ_ITEM_DEFS.find((d) => d.id === initialItemId);
      if (def) setCategoryId(def.categoryId);
      return;
    }
    if (!initialQuery.trim()) return;
    const items = searchFaqItems(t, initialQuery, 'all');
    if (items.length > 0) {
      setOpenIds(new Set([items[0].id]));
      setScrollTargetId(items[0].id);
    }
  }, [initialQuery, initialItemId, t, i18n.language]);

  useEffect(() => {
    if (!scrollTargetId) return;
    const el = itemRefs.current[scrollTargetId];
    if (!el) return;
    const timer = window.setTimeout(() => {
      const isMobileViewport = window.matchMedia('(max-width: 767px)').matches;
      el.scrollIntoView({ behavior: 'smooth', block: isMobileViewport ? 'start' : 'nearest' });
      setScrollTargetId(null);
    }, 120);
    return () => window.clearTimeout(timer);
  }, [scrollTargetId, openIds]);

  const syncPageUrl = useCallback(
    (opts: { id?: string; q?: string; cat?: string }) => {
      if (variant !== 'page') return;
      const params = new URLSearchParams();
      if (opts.id) params.set('id', opts.id);
      else if (opts.q?.trim()) params.set('q', opts.q.trim());
      else if (opts.cat && opts.cat !== 'all') params.set('cat', opts.cat);
      const next = params.toString() ? `/faq?${params.toString()}` : '/faq';
      const current = `${window.location.pathname}${window.location.search}`;
      if (current !== next) window.history.replaceState(null, '', next);
    },
    [variant],
  );

  const toggle = useCallback((id: string) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
        setScrollTargetId(id);
        syncPageUrl({ id });
      }
      return next;
    });
  }, [syncPageUrl]);

  const expandAll = useCallback(() => {
    setOpenIds(new Set(filteredIds));
  }, [filteredIds]);

  const collapseAll = useCallback(() => {
    setOpenIds(new Set());
  }, []);

  const registerRef = useCallback((id: string, el: HTMLElement | null) => {
    itemRefs.current[id] = el;
  }, []);

  const openRelated = useCallback((id: string) => {
    setOpenIds((prev) => new Set(prev).add(id));
    setScrollTargetId(id);
    const def = FAQ_ITEM_DEFS.find((d) => d.id === id);
    if (def) setCategoryId(def.categoryId);
    syncPageUrl({ id });
  }, [syncPageUrl]);

  const jumpTo = useCallback((id: string) => {
    setOpenIds((prev) => new Set(prev).add(id));
    setScrollTargetId(id);
    syncPageUrl({ id });
  }, [syncPageUrl]);

  useEffect(() => {
    const onBeforePrint = () => setOpenIds(new Set(filteredIds));
    window.addEventListener('beforeprint', onBeforePrint);
    return () => window.removeEventListener('beforeprint', onBeforePrint);
  }, [filteredIds]);

  useEffect(() => {
    if (variant !== 'page') return;
    const timer = window.setTimeout(() => {
      syncPageUrl({ q: query, cat: categoryId });
    }, 280);
    return () => window.clearTimeout(timer);
  }, [query, categoryId, variant, syncPageUrl]);

  useEffect(() => {
    if (variant !== 'page') return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/') return;
      const target = e.target;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        (target instanceof HTMLElement && target.isContentEditable)
      ) {
        return;
      }
      e.preventDefault();
      searchRef.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [variant]);

  return (
    <div
      id="saas-faq-explorer"
      className={`saas-faq-exp${variant === 'modal' ? ' saas-faq-exp--modal' : ''}`}
    >
      <div className="saas-faq-exp__head">
        <div className="saas-faq-exp__search-wrap">
          <span className="saas-faq-exp__search-icon" aria-hidden="true">🔍</span>
          <input
            ref={searchRef}
            id={`${baseId}-faq-search`}
            type="search"
            className="saas-faq-exp__search"
            placeholder={t('faq.searchPlaceholder')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoComplete="off"
            aria-label={t('faq.searchAria')}
          />
          {query.trim() ? (
            <button
              type="button"
              className="saas-faq-exp__clear"
              onClick={() => setQuery('')}
              aria-label={t('faq.clearSearch')}
            >
              ×
            </button>
          ) : null}
          <p className="saas-faq-exp__hint">
            {t('faq.searchHint')}
            {variant === 'page' ? ` · ${t('faq.searchFocusKey')}` : ''}
          </p>
        </div>

        <div className="saas-faq-exp__cats scroll-strip" role="tablist" aria-label={t('faq.categoriesAria')}>
          {FAQ_CATEGORIES.map((c) => (
            <button
              key={c.id}
              type="button"
              role="tab"
              aria-selected={categoryId === c.id}
              className={`saas-faq-exp__cat${categoryId === c.id ? ' saas-faq-exp__cat--active' : ''}`}
              onClick={() => {
                setCategoryId(c.id);
                if (c.id !== categoryId) setQuery('');
                syncPageUrl({ cat: c.id });
              }}
            >
              <span aria-hidden="true">{c.icon}</span>
              {t(`faq.categories.${c.id}`, { defaultValue: c.label })}
            </button>
          ))}
        </div>

        <div className="saas-faq-exp__toolbar">
          <span className="saas-faq-exp__count">
            {filtered.length}{' '}
            {filtered.length === 1 ? t('faq.answerSingular') : t('faq.answerPlural')}
            {query.trim() ? ` ${t('faq.matching')} “${query.trim()}”` : ''}
            {categoryId !== 'all'
              ? ` · ${t(`faq.categories.${categoryId}`, { defaultValue: FAQ_CATEGORIES.find((c) => c.id === categoryId)?.label ?? '' })}`
              : ''}
          </span>
          {filtered.length > 0 && (
            <div className="saas-faq-exp__bulk" role="group" aria-label={t('faq.bulkAria')}>
              <button
                type="button"
                className="saas-faq-exp__bulk-btn"
                onClick={expandAll}
                disabled={allFilteredOpen}
              >
                {t('faq.expandAll')}
              </button>
              <button
                type="button"
                className="saas-faq-exp__bulk-btn"
                onClick={collapseAll}
                disabled={openIds.size === 0}
              >
                {t('faq.collapseAll')}
              </button>
            </div>
          )}
        </div>
      </div>

      <div
        className={`saas-faq-exp__body${variant === 'page' && filtered.length >= 4 ? ' saas-faq-exp__body--with-sidebar' : ''}`}
      >
        {variant === 'page' && filtered.length >= 4 && (
          <nav className="saas-faq-exp__sidebar" aria-label={t('faq.jumpAria')}>
            <span className="saas-faq-exp__jump-label">{t('faq.jumpLabel')}</span>
            <ul className="saas-faq-exp__jump-list">
              {filtered.map((item) => (
                <li key={item.id}>
                  <button type="button" className="saas-faq-exp__jump-link" onClick={() => jumpTo(item.id)}>
                    {highlightMatch(item.question, query)}
                  </button>
                </li>
              ))}
            </ul>
          </nav>
        )}

        <div className="saas-faq-exp__main">
          {filtered.length === 0 ? (
            <div className="saas-faq-exp__empty">
              <p>{t('faq.emptyMsg')}</p>
              <p className="saas-faq-exp__empty-hint">{t('faq.emptyTry')}</p>
              <ul className="saas-faq-exp__empty-links">
                {FAQ_POPULAR_IDS.slice(0, 4).map((id) => {
                  const item = resolveFaqItemById(t, id);
                  if (!item) return null;
                  if (variant === 'modal') {
                    return (
                      <li key={id}>
                        <button type="button" className="saas-faq-exp__empty-link" onClick={() => jumpTo(id)}>
                          {item.question}
                        </button>
                      </li>
                    );
                  }
                  return (
                    <li key={id}>
                      <Link to={`/faq?id=${id}`} className="saas-faq-exp__empty-link">
                        {item.question}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : (
            <div className="saas-faq-exp__list" role="list">
              {filtered.map((item, index) => (
                <FaqAccordionRow
                  key={item.id}
                  item={item}
                  index={index}
                  isOpen={openIds.has(item.id)}
                  onToggle={() => toggle(item.id)}
                  qIcon={categoryIcon(item.categoryId)}
                  baseId={baseId}
                  registerRef={registerRef}
                  variant={variant}
                  onOpenRelated={openRelated}
                  searchQuery={query}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FaqAccordionRow({
  item,
  index,
  isOpen,
  onToggle,
  qIcon,
  baseId,
  registerRef,
  variant,
  onOpenRelated,
  searchQuery,
}: {
  item: FaqItem;
  index: number;
  isOpen: boolean;
  onToggle: () => void;
  qIcon: string;
  baseId: string;
  registerRef: (id: string, el: HTMLElement | null) => void;
  variant: 'page' | 'modal';
  onOpenRelated: (id: string) => void;
  searchQuery: string;
}) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const panelId = `${baseId}-panel-${item.id}`;
  const headerId = `${baseId}-header-${item.id}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(faqItemUrl(item.id));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked */
    }
  };

  return (
    <article
      ref={(el) => registerRef(item.id, el)}
      id={`faq-item-${item.id}`}
      className={`saas-faq-exp__item${isOpen ? ' saas-faq-exp__item--open' : ''}`}
      style={{ animationDelay: `${Math.min(index, 12) * 40}ms` }}
      role="listitem"
    >
      <button
        type="button"
        id={headerId}
        className="saas-faq-exp__trigger"
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={onToggle}
      >
        <span className="saas-faq-exp__q-icon" aria-hidden="true">
          {qIcon}
        </span>
        <span className="saas-faq-exp__q-text">{highlightMatch(item.question, searchQuery)}</span>
      </button>
      <div className="saas-faq-exp__panel" id={panelId} role="region" aria-labelledby={headerId}>
        <div className="saas-faq-exp__panel-inner">
          <div className="saas-faq-exp__answer">
            <div className="saas-faq-exp__answer-grid">
              <div className="saas-faq-exp__answer-copy">
                {item.paragraphs.map((p, i) => (
                  <div key={i} className="saas-faq-exp__para-block">
                    {item.paragraphs.length > 1 ? (
                      <span className="saas-faq-exp__para-label">
                        {t(`faq.paraLabel.${i}`, { defaultValue: FAQ_PARA_LABELS[i] ?? 'Note' })}
                      </span>
                    ) : null}
                    <p className="saas-faq-exp__para">
                      {isOpen ? highlightMatch(p, searchQuery) : p}
                    </p>
                  </div>
                ))}
                {item.learnMore && (
                  <Link to={item.learnMore.to} className="saas-faq-exp__link">
                    {item.learnMore.label} <span aria-hidden="true">→</span>
                  </Link>
                )}
                <div className="saas-faq-exp__actions">
                  <button type="button" className="saas-faq-exp__copy-link" onClick={() => void copyLink()}>
                    {copied ? t('faq.copyLinkDone') : t('faq.copyLink')}
                  </button>
                </div>
                {item.relatedIds.length > 0 && (
                  <div className="saas-faq-exp__related" aria-label={t('faq.relatedAria')}>
                    <span className="saas-faq-exp__related-label">{t('faq.relatedLabel')}</span>
                    <div className="saas-faq-exp__related-links">
                      {item.relatedIds.map((relId) => {
                        const rel = resolveFaqItemById(t, relId);
                        if (!rel) return null;
                        if (variant === 'modal') {
                          return (
                            <button
                              key={relId}
                              type="button"
                              className="saas-faq-exp__related-link"
                              onClick={() => onOpenRelated(relId)}
                            >
                              {rel.question}
                            </button>
                          );
                        }
                        return (
                          <Link key={relId} to={`/faq?id=${relId}`} className="saas-faq-exp__related-link">
                            {rel.question}
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                )}
                <div className="saas-faq-exp__tag-row" aria-label={t('faq.keywordsAria')}>
                  {item.keywords.split(/\s+/).slice(0, 6).map((kw) => (
                    <span key={kw} className="saas-faq-exp__tag">
                      {kw}
                    </span>
                  ))}
                </div>
              </div>
              {item.visual !== 'none' && (
                <div className="saas-faq-exp__answer-visual">
                  <FaqVisual type={item.visual} />
                  <span className="saas-faq-exp__visual-caption">
                    {t(`faq.visual.${item.visual}`, { defaultValue: FAQ_VISUAL_LABELS[item.visual] ?? item.visual })}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}
